import { useState, useEffect, useCallback } from 'react'
import { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'
import type { AdminUser, AdminRole, CustomerProfile } from '@/types/database'

interface AuthState {
  user: User | null
  session: Session | null
  adminUser: AdminUser | null
  customerProfile: CustomerProfile | null
  isLoading: boolean
  isAdmin: boolean
  adminRole: AdminRole | null
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    adminUser: null,
    customerProfile: null,
    isLoading: true,
    isAdmin: false,
    adminRole: null,
  })

  const fetchUserData = useCallback(async (user: User | null) => {
    if (!user) {
      setState({
        user: null,
        session: null,
        adminUser: null,
        customerProfile: null,
        isLoading: false,
        isAdmin: false,
        adminRole: null,
      })
      return
    }

    // Fetch admin user and customer profile in parallel
    // Use maybeSingle() to return null instead of error when no rows found
    const [adminResult, profileResult] = await Promise.all([
      supabase
        .from('admin_users')
        .select('*')
        .eq('id', user.id)
        .maybeSingle(),
      supabase
        .from('customer_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle(),
    ])

    const adminUser = adminResult.data as AdminUser | null
    const customerProfile = profileResult.data as CustomerProfile | null

    setState((prev) => ({
      ...prev,
      user,
      adminUser,
      customerProfile,
      isLoading: false,
      isAdmin: !!adminUser,
      adminRole: adminUser?.role || null,
    }))

    // Update last login for admin users (fire and forget)
    if (adminUser) {
      supabase
        .from('admin_users')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', user.id)
        .then(() => {})
    }
  }, [])

  const syncAdminMembership = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from('admin_users')
      .select('*')
      .eq('id', userId)
      .maybeSingle()

    const nextAdminUser = data as AdminUser | null

    setState((prev) => {
      if (prev.user?.id !== userId) return prev
      return {
        ...prev,
        adminUser: nextAdminUser,
        isAdmin: !!nextAdminUser,
        adminRole: nextAdminUser?.role || null,
      }
    })
  }, [])

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setState((prev) => ({ ...prev, session }))
      fetchUserData(session?.user ?? null)
    })

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setState((prev) => ({ ...prev, session }))

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          fetchUserData(session?.user ?? null)
        } else if (event === 'SIGNED_OUT') {
          setState({
            user: null,
            session: null,
            adminUser: null,
            customerProfile: null,
            isLoading: false,
            isAdmin: false,
            adminRole: null,
          })
        }
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [fetchUserData])

  useEffect(() => {
    const userId = state.user?.id
    if (!userId) return

    const channel = supabase
      .channel(`auth-admin-user-${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'admin_users' },
        (payload) => {
          const changedRecord = (payload.eventType === 'DELETE' ? payload.old : payload.new) as { id?: string } | null
          if (!changedRecord?.id || changedRecord.id !== userId) {
            return
          }

          if (payload.eventType === 'DELETE') {
            setState((prev) => {
              if (prev.user?.id !== userId) return prev
              return {
                ...prev,
                adminUser: null,
                isAdmin: false,
                adminRole: null,
              }
            })
            return
          }

          const nextAdminUser = payload.new as AdminUser
          setState((prev) => {
            if (prev.user?.id !== userId) return prev
            return {
              ...prev,
              adminUser: nextAdminUser,
              isAdmin: true,
              adminRole: nextAdminUser.role,
            }
          })
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          void syncAdminMembership(userId)
        }
      })

    return () => {
      supabase.removeChannel(channel)
    }
  }, [state.user?.id, syncAdminMembership])

  useEffect(() => {
    const userId = state.user?.id
    if (!userId) return

    const syncIfVisible = () => {
      if (document.visibilityState === 'visible') {
        void syncAdminMembership(userId)
      }
    }

    window.addEventListener('focus', syncIfVisible)
    document.addEventListener('visibilitychange', syncIfVisible)

    return () => {
      window.removeEventListener('focus', syncIfVisible)
      document.removeEventListener('visibilitychange', syncIfVisible)
    }
  }, [state.user?.id, syncAdminMembership])

  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  }, [])

  const signUp = useCallback(async (email: string, password: string, metadata?: { display_name?: string }) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${import.meta.env.VITE_SITE_URL}/auth/callback`,
        data: metadata,
      },
    })
    return { data, error }
  }, [])

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }, [])

  const resetPassword = useCallback(async (email: string) => {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${import.meta.env.VITE_SITE_URL}/auth/reset-password`,
    })
    return { data, error }
  }, [])

  const updatePassword = useCallback(async (password: string) => {
    const { data, error } = await supabase.auth.updateUser({ password })
    return { data, error }
  }, [])

  const refreshSession = useCallback(async () => {
    const { data, error } = await supabase.auth.refreshSession()
    if (data.session) {
      setState((prev) => ({ ...prev, session: data.session }))
      fetchUserData(data.session.user)
    }
    return { data, error }
  }, [fetchUserData])

  // Check if user has at least the required role level
  const hasRoleLevel = useCallback((requiredRole: AdminRole): boolean => {
    if (!state.adminRole) return false

    const roleHierarchy: Record<AdminRole, number> = {
      owner: 3,
      admin: 2,
      editor: 1,
    }

    return roleHierarchy[state.adminRole] >= roleHierarchy[requiredRole]
  }, [state.adminRole])

  return {
    ...state,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    refreshSession,
    hasRoleLevel,
  }
}
